import { useLazyQuery } from "@apollo/client";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Company } from "../../__generated__/graphql";
import { GET_COMPANY } from "../../features/graphql/query/company";

export interface CompanyContextType {
    company: CompanyDataType,
    setCompany: (company: CompanyDataType) => void
}
type CompanyDataType = Company | null;
export const CompanyContext = React.createContext<CompanyContextType | null>(null);

CompanyContext.displayName = "CompanyContext";

export interface CompanyProviderProps {
    children: React.ReactNode
}

export const CompanyProvider = ({ children }: CompanyProviderProps) => {
    const [company, setCompany] = useState<CompanyDataType>(null);

    const memorizedValue = useMemo(() => ({
        company, setCompany
    }), [company]);

    return (
        <CompanyContext.Provider value={memorizedValue}>
            {children}
        </CompanyContext.Provider>
    )
}

export const useCompanyContext = () => {
    const context = useContext(CompanyContext);
    if (!context)
        throw new Error('Call made outside provider. You must use useSelectPersonContext within the SelectedPersonProvider provider.');
    return context;
}

interface SelectedCompany {
    loading: boolean,
    company: CompanyDataType,
    setCompany: (company: CompanyDataType) => void,
    fetchCompany: (id: string) => void
}

export const useCompany = (id?: string | null): SelectedCompany => {
    const { company, setCompany } = useCompanyContext();
    const [fetch, { data, loading }] = useLazyQuery(GET_COMPANY, {
        notifyOnNetworkStatusChange: true
    });
    const fetchCompany = (id: string) => {
        fetch({ variables: { id } })
    };

    useEffect(() => {
        if (!id || company) return;
        fetchCompany(id);
    }, [id, company]);

    useEffect(() => {

        if (loading || !data) return;
        const _company = data?.company
        setCompany(_company)
    }, [loading, data])

    return { loading, company, setCompany, fetchCompany }
}
